import { cn } from "@/lib/utils";
import { DemoBadge } from "@/components/DemoBadge";
import { Loader2, Inbox, AlertTriangle } from "lucide-react";

export function PageHeader({ title, subtitle, icon: Icon, demo = true, actions, testId }) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4 mb-5" data-testid={testId}>
      <div className="flex items-start gap-3">
        {Icon && (
          <div className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
            <Icon className="w-5 h-5 text-blue-400" />
          </div>
        )}
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-xl font-bold text-white tracking-tight">{title}</h1>
            {demo && <DemoBadge />}
          </div>
          {subtitle && <p className="text-xs text-slate-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

export function Panel({ title, children, className, actions, testId }) {
  return (
    <div
      data-testid={testId}
      className={cn(
        "rounded-lg border border-[var(--border-subtle)] bg-[var(--card)] overflow-hidden",
        className
      )}
    >
      {title && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--border-subtle)]">
          <h3 className="text-sm font-semibold text-slate-200">{title}</h3>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
}

export function LoadingState({ label = "Loading..." }) {
  return (
    <div data-testid="loading-state" className="flex flex-col items-center justify-center py-20 text-slate-500">
      <Loader2 className="w-6 h-6 animate-spin mb-3" />
      <span className="text-xs">{label}</span>
    </div>
  );
}

export function EmptyState({ label = "No data available", icon: Icon = Inbox }) {
  return (
    <div data-testid="empty-state" className="flex flex-col items-center justify-center py-16 text-slate-600">
      <Icon className="w-8 h-8 mb-3" />
      <span className="text-xs">{label}</span>
    </div>
  );
}

export function ErrorState({ label = "Something went wrong", onRetry }) {
  return (
    <div data-testid="error-state" className="flex flex-col items-center justify-center py-16 text-rose-400">
      <AlertTriangle className="w-8 h-8 mb-3" />
      <span className="text-xs mb-3">{label}</span>
      {onRetry && (
        <button onClick={onRetry} className="text-xs px-3 py-1.5 rounded-md border border-rose-500/30 hover:bg-rose-500/10">
          Retry
        </button>
      )}
    </div>
  );
}

export function Delta({ value, pct, className }) {
  const n = Number(value ?? pct);
  const positive = n >= 0;
  return (
    <span
      className={cn(
        "font-mono-num text-xs",
        positive ? "text-emerald-400" : "text-rose-400",
        className
      )}
    >
      {positive ? "▲" : "▼"} {pct != null ? `${positive ? "+" : ""}${Number(pct).toFixed(2)}%` : value}
    </span>
  );
}
